<template>
  <div
    v-if="uploadedImage === null"
    class="onboarding-wizard-idea-cover-wrapper font-outfit-light cursor-pointer"
    :class="{ 'add-border': croppedImage === null }"
    :style="{ backgroundImage: `url(${coverImage})`}"
    @click="openImageUploader"
  >
    <img v-if="!croppedImage" class="cover-icon" :src="icon" alt="alt" width="40">

    <div v-show="croppedImage" class="avatar-container" @click.stop="remove">
      <span>
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" d="M15 4h4a1 1 0 110 2h-1v13a3 3 0 01-3 3H5a3 3 0 01-3-3V6H1a1 1 0 110-2h4V3a3 3 0 013-3h4a3 3 0 013 3v1zm-2 0V3a1 1 0 00-1-1H8a1 1 0 00-1 1v1h6zm3 2H4v13a1 1 0 001 1h10a1 1 0 001-1V6zm-9 4a1 1 0 112 0v6a1 1 0 11-2 0v-6zm4 0a1 1 0 112 0v6a1 1 0 11-2 0v-6z"/></svg>
      </span>
    </div>

    <ib-tooltip
      v-if="!croppedImage"
      placement="top-end"
      popper-class="medium center"
      :content="$t('uploadOwnPictureOrLogo')"
    >
      <!-- Plus Button -->
      <div class="plus-btn position-absolute">
        <i class="el-icon-plus" />
      </div>
      <!-- /Plus Button -->
    </ib-tooltip>

    <input
      id="upload-img"
      ref="imageUploader"
      class="d-none"
      type="file"
      accept="image/*"
      @change="setImage"
    >
  </div>
  <div v-else>
    <vue-cropper
      v-show="uploadedImage"
      ref="cropper"
      :class="{ 'unhidden': uploadedImage }"
      class="cropper"
      :guides="true"
      :view-mode="0"
      drag-mode="crop"
      :auto-crop-area="1"
      :min-container-width="287"
      :min-container-height="288"
      :background="true"
      :output-type="'png'"
      :src="uploadedImage"
      alt=""
      :img-style="{ 'width': '287px', 'height': '282px' }"
    />

    <div v-if="uploadedImage" class="d-flex justify-center mt-3">
      <ib-button size="sm" submit-button variant="red" @click="cancel">
        {{ $t('cancel') }}
      </ib-button>
      <ib-button size="sm" submit-button class="ml-2" @click="cropImage">
        {{ $t('save') }}
      </ib-button>
    </div>
  </div>
</template>

<script>
import 'cropperjs/dist/cropper.css'
import VueCropper from 'vue-cropperjs'

export default {
  name: 'OnboardingWizardIdeaCover',

  components: {
    VueCropper
  },

  props: {
    cover: {
      type: String,
      required: true
    },

    icon: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      uploadedImage: null,
      croppedImage: null
    }
  },

  computed: {
    coverImage () {
      return this.croppedImage ? this.croppedImage : this.cover
    }
  },

  methods: {
    openImageUploader () {
      if (this.croppedImage || this.uploadedImage) return
      this.$refs.imageUploader.click()
    },

    setImage (e) {
      const file = e.target.files[0]

      const MAX_SIZE = 5 * 1024 * 1024
      const allowedTypes = ['image/jpeg', 'image/png']

      if (!allowedTypes.includes(file.type)) {
        this.$notify({ message: this.$t('validationMessages.pictureMustBeJpgOrPngFormat'), type: 'error' })
        e.target.value = ''

        return
      }

      if (file.size > MAX_SIZE) {
        this.$notify({ message: this.$t('validationMessages.pictureSizeCanNotExceed', { size: '5MB' }), type: 'error' })
        e.target.value = ''

        return
      }

      if (typeof FileReader === 'function') {
        const reader = new FileReader()
        reader.onload = (event) => {
          this.uploadedImage = event.target.result
        }
        reader.readAsDataURL(file)
      } else {
        alert('Sorry, FileReader API not supported')
      }
    },

    cropImage () {
      const imageBase64 = this.$refs.cropper.getCroppedCanvas().toDataURL()
      if (imageBase64.startsWith('data:image/')) {
        this.croppedImage = imageBase64
        this.uploadedImage = null
        this.$emit('update:cover', this.croppedImage)
      }
    },

    cancel () {
      this.uploadedImage = null
    },

    remove () {
      this.croppedImage = null
      this.uploadedImage = null
      this.$emit('update:cover', null)
    }
  }
}
</script>

<style scoped lang="scss">
.onboarding-wizard-idea-cover-wrapper {
  position: relative;
  max-width: 287px;
  margin: 0 auto;
  height: 282px;
  border-radius: 8px;
  border: 1px solid $color-border;
  background-position: center;
  background-size: contain;
  justify-content: center;

  .cover-icon {
    position: absolute;
    bottom: 10px;
    left: 10px;
    border-radius: 8px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.14), 0 0 2px 0 rgba(0, 0, 0, 0.12);
    padding: 5px;
  }

  .plus-btn {
    width: 30px;
    height: 30px;
    padding: 8px;
    border-radius: 50%;
    background-color: var(--color-primary);
    color: #fff;
    position: absolute;
    bottom: 10px;
    right: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.25s;
  }

  .avatar-container {
    cursor: pointer;
    border-radius: 4.5px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    overflow: hidden;
    height: 282px !important;

    &.disable {
      cursor: not-allowed;
    }

    &:not(.disable) {
      &:hover {
        span {
          opacity: 1;
        }
      }
    }

    span {
      background-color: fade-out($color-navy, 0.25);
      opacity: 0;
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.3s;

      img, svg {
        path {
          fill: $color-white;
        }
      }
    }
  }

  &:hover {
    .plus-btn {
      transform: translate(-4px, -4px);
    }
  }
}
</style>
